import React from 'react';
import {QuestionReport} from '../../../incase-domain/analytics';
import {ELEMENT} from '../../../incase-domain/elements/base/ElementTypes';
import ReportAnswerPercentage from '../../../incase-domain/components/report/answer/ReportAnswerPercentage';
import ConfusionMatrix from '../../../incase-domain/components/report/answer/matchPair/confusionMatrix/ConfusionMatrix';
import ReportAnswerLikertScale from '../../../incase-domain/components/report/answer/likertScale/ReportAnswerLikertScale';
import Button from '../../../incase-domain/components/button/Button';
import {getElementData} from '../../../incase-domain/elements/base/elementsData';
import AccordionItem from '../AccordionItem';
import {getAnswerStatus} from '../../../utils/report';
import ImageMarkupHeatmap from './ImageMarkupHeatmap';
import SequenceReport from '../../pages/PresentationReportsPage/questions/SequenceReport';

interface QuestionReportGraphProps {
    questionReport: QuestionReport;
    open?: boolean;
    disableColorSlider?: boolean;
}

function QuestionReportGraph(props: QuestionReportGraphProps): JSX.Element {
    const {questionReport, open, disableColorSlider} = props;

    const renderStandardQuestions = (questionReport: QuestionReport): JSX.Element => {
        return (
            <div className="u-flex u-flex-col u-gap-1">
                {questionReport?.answers?.map((answer, index) => (
                    <ReportAnswerPercentage
                        key={index}
                        label={answer.answerText}
                        percentage={answer.percentage}
                        type={getAnswerStatus(answer, questionReport)}
                        showCorrect
                        isPresenter
                        fullColor={disableColorSlider}
                    />
                ))}{' '}
            </div>
        );
    };

    const renderSequenceQuestion = (questionReport: QuestionReport): JSX.Element => {
        return <SequenceReport questionReport={questionReport} disableColorSlider={disableColorSlider} />;
    };

    const renderMatchPairQuestion = (questionReport: QuestionReport): JSX.Element | null => {
        if (!questionReport.matchPairsReport) return null;
        return <ConfusionMatrix matchPairReport={questionReport.matchPairsReport} showPercentages />;
    };

    const renderLikertScale = (questionReport: QuestionReport): JSX.Element | null => {
        return <ReportAnswerLikertScale questionReport={questionReport} />;
    };

    const renderImageMarkup = (questionReport: QuestionReport): JSX.Element => {
        return (
            <div className="u-flex u-flex-col u-gap-2">
                <ImageMarkupHeatmap elementId={questionReport.elementId} points={questionReport.imageMarkupPoints} />
                {renderStandardQuestions(questionReport)}
            </div>
        );
    };

    const STANDARD_QUESTION_TYPES = [
        ELEMENT.QUESTION,
        ELEMENT.YES_NO_QUESTION,
        ELEMENT.YES_NO_QUESTION_V2,
        ELEMENT.MULTI_SELECT,
        ELEMENT.PHOTO_ANSWER_QUESTION,
    ];

    type QuestionRenderer = (questionReport: QuestionReport) => JSX.Element | null;

    const QUESTION_RENDERERS: Partial<Record<ELEMENT, QuestionRenderer>> = {
        ...STANDARD_QUESTION_TYPES.reduce(
            (acc, type) => ({...acc, [type]: renderStandardQuestions}),
            {} as Record<ELEMENT, QuestionRenderer>
        ),
        [ELEMENT.SEQUENCE_QUESTION]: renderSequenceQuestion,
        [ELEMENT.IMAGE_MARKUP]: renderImageMarkup,
        [ELEMENT.MATCH_PAIR_QUESTION]: renderMatchPairQuestion,
        [ELEMENT.LIKERT_SCALE]: renderLikertScale,
    };

    const isSequenceQuestion = questionReport.type === ELEMENT.SEQUENCE_QUESTION || String(questionReport.type) === 'SEQUENCE_QUESTION';
    const renderer = isSequenceQuestion ? renderSequenceQuestion : QUESTION_RENDERERS[questionReport.type];
    const element = renderer ? renderer(questionReport) : null;

    return (
        <div>
            <AccordionItem
                modifiers={['tertiary']}
                header={
                    <div className="questionReportGraph__header">
                        <Button leading={getElementData(questionReport.type)?.icon} variant="ghost" disablePointerEvents size="md">
                            {questionReport.question}
                        </Button>
                        <Button leading="solar:users-group-rounded-linear" variant="ghost" disablePointerEvents size="md">
                            {questionReport.totalAnswers}
                        </Button>
                    </div>
                }
                open={open}
            >
                <div className="questionReportGraph__body">{element}</div>
            </AccordionItem>
        </div>
    );
}

export default QuestionReportGraph;
